import React, { Fragment, useState } from 'react';
import { FormattedNumber } from 'react-intl';

import InvoiceItemsList from 'components/Core/Invoice/InvoiceItemsList/InvoiceItemsList';
import { getDisplayableLineItems } from 'components/Core/Invoice/utils/lineItemsValidationHelper';
import DataRow from 'components/Shared/DataRow/DataRow';
import ExtendBtn from 'components/Shared/ExtendBtn/ExtendBtn';
import Hr from 'components/Shared/Hr/Hr';
import { useIsMobileScreen } from 'hooks/useIsMobile';
import { useRHSExperiment } from 'hooks/useRHSExperiment';
import { colors, fontSize } from 'styles/cp';
import { LineItem } from 'types/Sale';

export interface InvoiceLineItemsProps {
  invoiceLineItems?: LineItem[];
  currency?: string;
  subTotalAmount: number;
  discountAmount: number;
  shippingAmount: number;
  invoiceTaxAmount?: number;
  hrColor: string;
  showTopHr?: boolean;
  showBottomTotal?: boolean;
}

const InvoiceLineItems = ({
  invoiceLineItems,
  currency,
  subTotalAmount,
  discountAmount,
  shippingAmount,
  invoiceTaxAmount,
  hrColor,
  showTopHr = true,
}: InvoiceLineItemsProps) => {
  const filteredLineItems = getDisplayableLineItems(invoiceLineItems);
  const isMobile = useIsMobileScreen();
  const { isRHSExperimentBreakdownButtonEnabled } = useRHSExperiment();

  // Expand by default when experiment is active (IXP2_T_1065836) in non-mobile
  const shouldShowAllLineItems = isRHSExperimentBreakdownButtonEnabled && !isMobile;
  const [isShowFullInvoiceItems, setIsShowFullInvoiceItems] = useState(shouldShowAllLineItems);

  const hasDisplayableLineItems = filteredLineItems?.length > 0;
  const hasMoreThanSixItems = filteredLineItems?.length > 6;
  const isExperimentTreatment = isRHSExperimentBreakdownButtonEnabled && !isMobile;

  // Show extra items when: naturally expanded OR experiment treatment is active
  const shouldShowExtraItems = hasMoreThanSixItems || isExperimentTreatment;

  // Show toggle button if: natural pagination (7+ items) AND NOT the specific experiment treatment
  // For IXP2_T_1065836 in non-mobile: hide toggle completely (items stay expanded)
  const shouldShowToggleButton = hasMoreThanSixItems && !isExperimentTreatment;

  const shouldShowTopHr = hasDisplayableLineItems && showTopHr;
  const shouldShowLineItems = hasDisplayableLineItems;

  const firstLineItems = filteredLineItems?.slice(0, 6);
  const extraLineItems = filteredLineItems?.slice(6);

  return (
    <Fragment>
      <style jsx>{`
        .w-open {
          max-height: ${isShowFullInvoiceItems ? '3000px' : 0};
          transition: all 0.35s ease-in-out;
          overflow: ${isShowFullInvoiceItems ? 'visible' : 'hidden'};
        }
        .expanded-label-wrapper {
          display: flex;
          margin-bottom: 11px;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
        }
        .label {
          font-size: ${fontSize.xs};
          color: ${colors.gray02};
          font-family: AvenirNextforINTUIT-Regular;
        }
      `}</style>
      {shouldShowTopHr && (
        <Hr marginTop={0} marginBottom={11} opacity={1} borderColor={hrColor} borderThickness={1} />
      )}
      {shouldShowLineItems && <InvoiceItemsList lineItems={firstLineItems} currency={currency} />}
      {shouldShowExtraItems && isShowFullInvoiceItems && (
        <div className="w-open">
          <InvoiceItemsList lineItems={extraLineItems} currency={currency} />
        </div>
      )}
      {shouldShowToggleButton && (
        <div
          className="expanded-label-wrapper"
          onClick={() => setIsShowFullInvoiceItems(!isShowFullInvoiceItems)}
        >
          <span className="label left" title="expanded-label">
            {isShowFullInvoiceItems ? 'Hide' : 'Show all items'}
          </span>
          <ExtendBtn
            opened={isShowFullInvoiceItems}
            handler={() => setIsShowFullInvoiceItems(!isShowFullInvoiceItems)}
            margin="unset"
            ariaLabel={isShowFullInvoiceItems ? 'Hide all items' : 'Show all items'}
          />
        </div>
      )}
      <Hr marginTop={0} marginBottom={11} opacity={1} borderColor={hrColor} borderThickness={1} />
      <DataRow
        idValue="SUBTOTAL"
        defaultMessage="Subtotal"
        value={<FormattedNumber value={subTotalAmount} style="currency" currency={currency} />}
      />
      {!!discountAmount && discountAmount > 0 && (
        <DataRow
          idValue="DISCOUNT"
          defaultMessage="Discount"
          value={
            <FormattedNumber
              value={`-${discountAmount}` as unknown as number}
              style="currency"
              currency={currency}
            />
          }
        />
      )}
      {!!shippingAmount && shippingAmount > 0 && (
        <DataRow
          idValue="SHIPPING"
          defaultMessage="Shipping"
          value={<FormattedNumber value={shippingAmount} style="currency" currency={currency} />}
        />
      )}
      {!!invoiceTaxAmount && invoiceTaxAmount > 0 && (
        <DataRow
          idValue="INVOICE_SUMMARY_INVOICE_TAX"
          defaultMessage="Tax"
          value={<FormattedNumber value={invoiceTaxAmount} style="currency" currency={currency} />}
        />
      )}
    </Fragment>
  );
};

export default InvoiceLineItems;
